import { Close, Save } from '@mui/icons-material';
import {
  Alert,
  Box,
  ButtonGroup,
  Divider,
  Grid,
  Stack,
  Typography,
  Button as MuiButton
} from '@mui/material';
import { useState } from 'react';
import { Button, ExternalLink } from '../../components';
import { CodeBlock } from './code-block';
import { handleCopyClick } from './constants';
import { StyleGuidePage } from './style-guide-page';

export const StyleGuideUIUXPatterns = () => {
  const [isCopied, setIsCopied] = useState<{
    [val: string]: boolean;
  }>({});

  return (
    <StyleGuidePage title="UI/UX Patterns">
      <Grid container mb={2}>
        <Grid item xs={12} lg={8}>
          <Box mb={1}>
            <Typography>
              During the course of development, there are certain UI/UX patterns that have been
              implemented with both MUI components and custom components that are worth taking note.
            </Typography>
          </Box>
          <Box mb={2}>
            <Alert severity="info">
              NOTE: This is a living style guide and is subject to grow and change. The patterns
              listed on this page will eventually turn into new sections of the Kitchen Sink
              Components documentation. Feel free to add other temporary documentation to this page
              in lieu of future expansion of the Kitchen Sink. Please provide Example buttons, when
              possible.
            </Alert>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Button Groups
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              Button groups are used often and are not only used in Page Title components, but also
              for things like Save/Cancel buttons. When implementing a Save/Cancel button on a form,
              be sure to stick with the global pattern set, and have the Save button to the left and
              the Cancel button to the right.
            </Typography>
            <Box marginTop="2rem" mb={1}>
              <ButtonGroup>
                <MuiButton type="submit" startIcon={<Save />} variant="contained" color="primary">
                  Save
                </MuiButton>
                <MuiButton
                  type="button"
                  variant="contained"
                  color="inherit"
                  startIcon={<Close />}
                  onClick={() => {}}
                >
                  Cancel
                </MuiButton>
              </ButtonGroup>
            </Box>
            <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
              &lt;Box marginTop="2rem"&gt; <br />
              &nbsp;&nbsp;&nbsp;&lt;ButtonGroup&gt; <br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;MuiButton type="submit"
              startIcon=&#123;&lt;Save /&gt;&#125; variant="contained" color="primary"&gt; Save
              &lt;/MuiButton&gt; <br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;MuiButton type="button" variant="contained"
              color="inherit" startIcon=&#123;&lt;Close /&gt;&#125; onClick=&#123;() =&gt;
              &#123;&#125;&#125; &gt; Cancel &lt;/MuiButton&gt;
              <br />
              &nbsp;&nbsp;&nbsp;&lt;/ButtonGroup&gt; <br />
              &lt;/Box&gt;
            </CodeBlock>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Multi-Select
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              Though there are multiple ways to implement a multi-select field, the prominant
              implementation for the application is by using an Autocomplete selector with Chips. To
              view the example, after following the link, click the Add Territory button in the top
              right of the screen. The Vendors field is an autocomplete with multiselect.
            </Typography>
            <Typography mt={0.5}>
              <Button
                variant="contained"
                color="primary"
                to="/admin/manage/territories"
                asLink
                id="multiselect-example"
              >
                Example
              </Button>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Tabs and Grids
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              In some cases, tabs are paired with grids and act as sort of filters. In many
              instances, tabs are used in addition to a traditional filter bar. For these instances,
              tabs should display above the filter bar, not below it.
            </Typography>
            <Typography mt={1}>
              In addition, there are now certain instances where there are large amounts of tabs. In
              these situations, we use the <em>variant="scrollable"</em> prop. Resize your browser
              to observe its behavior.
            </Typography>
            <Typography mt={0.5}>
              <Stack direction="row" gap={0.5}>
                <Button
                  variant="contained"
                  color="primary"
                  to="/admin/user-tools/users"
                  asLink
                  id="tabs-example"
                >
                  Example
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  to="/development/neighborhoods/109/general-information"
                  asLink
                  id="scrolling-tabs-example"
                >
                  Scroll Example
                </Button>
              </Stack>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Sort/View By Dropdowns and Grids
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              In certain instances, instead of providing sort on a column header, a separate sort
              dropdown is created. For these instances, when the layout permits and things aren't
              too chaotic/cluttered, the sort dropdown should be placed using the Page Title
              component.
            </Typography>
            <Typography mt={0.5}>
              <Button
                variant="contained"
                color="primary"
                to="/admin/financial/ach"
                asLink
                id="sort-example"
              >
                Example
              </Button>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              CSV and Bulk Update Grids
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              There is an existing instance of a grid that has a Bulk Update toolbar with a CSV
              generation button that can be found at the following example. NOTE: though the bulk
              update example has a CSV button, the latest button component can be found in Sales
              Reports.
            </Typography>
            <Typography mt={0.5}>
              <Stack direction="row" gap={0.5}>
                <Button
                  variant="contained"
                  color="primary"
                  to="/production/orders/order-management"
                  asLink
                  id="bulk-update-example"
                >
                  Example
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  to="/sales/sales-tools/reports"
                  asLink
                  id="csv-download-button-example"
                >
                  CSV Download Button Example
                </Button>
              </Stack>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Formik MUI Fields
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              Outside of a filter implementation, there are select field components for Formik
              implementations. These include, but are not limited to, Select, TextField, Switch,
              Async Autocomplete, etc. These include formik specific attributes and help reduce the
              need to define everything for each field by dynamically setting things based on the
              name prop. When implementing a form with Formik, be sure to use the Formik specific
              component to help keep code clean. These components live in the <em>formik-mui</em>{' '}
              folder of the project.
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Async Select and Autocomplete
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              For select/autocomplete implementations, there are select field components labelled
              Async. Async fields help reduce page/component clutter by grouping non-dependent calls
              and loading states for dropdown options within the field itself. For example, if a
              Select has no need to be updated based on the selection from another filter/field, use
              Async Select to bundle up GET calls and loading states.
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Commonly Used Dropdowns
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              Over the course of development, there have been repeat instances of certain filters
              and dropdowns, such as Territories. Components exist for quickly placing these
              dropdowns without the need to defined separate GET calls and loading states. These
              live in the <em>dropdowns</em> folder of the project.
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Date Range Picker
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              There is a Date Range Picker component available for use, which has mostly been used
              for filtering.
            </Typography>
            <Typography mt={0.5}>
              <Button
                variant="contained"
                color="primary"
                to="/vendors/contracts/in-progress"
                asLink
                id="date-range-filter-example"
              >
                Example
              </Button>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Image Upload
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              There are multiple instances of image/file uploads, however, the most common
              implementation can be found at the following example.
            </Typography>
            <Typography mt={0.5}>
              <Button
                variant="contained"
                color="primary"
                to="/production/orders/place-order"
                asLink
                id="image-upload-example"
              >
                Example
              </Button>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Slider Filter
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              There are multiple instances of image/file uploads, however, the most common
              implementation can be found at the following example.
            </Typography>
            <Typography mt={0.5}>
              <Button
                variant="contained"
                color="primary"
                to="/vendors/contracts/in-progress"
                asLink
                id="slider-filter-example"
              >
                Example
              </Button>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Desktop Accordion Layout/List
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              There are multiple instances where an accordion list is required to show a grouped and
              collapsible set of data (i.e. punchlists with to-do items). This layout is based on
              the Mobile Table component, where tabular data becomes stacked and condensed for a
              better mobile experience. Though there isn't currently a reusable component for such
              an implementation on desktop, the following is a great example.
            </Typography>
            <Typography mt={0.5} mb={1}>
              <Button
                variant="contained"
                color="primary"
                to="/production/punchlists/dashboard"
                asLink
                id="desktop-accordion-list-example"
              >
                Example
              </Button>
            </Typography>
            <Typography variant="h6" component="h3" color="primary">
              Styling Recommendations
            </Typography>
            <Typography>
              <ul>
                <li>
                  <Typography mb={1.5}>
                    <strong>AccordionDetails Background Color</strong>: The inner background color
                    of the AccordionDetails component can vary depending on necessity. Usually, on
                    desktop, the background color is white, since it better accommodates things like
                    input fields and complex content. Where data display is the goal (simple label
                    and value text pairings) the background color can be the typical light grey
                    (grey[100]).
                  </Typography>
                </li>
                <li>
                  <strong>AccordionDetails Content Display</strong>: The content for label/value
                  text should follow the styling implemented in the Mobile Table component. For a
                  visual reference go to the following example and shrink your screen until you get
                  a mobile view.
                  <Typography mt={0.5} mb={1.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      to="/development/framers"
                      asLink
                      id="mobile-table-example"
                    >
                      Example
                    </Button>
                  </Typography>
                </li>
                <li>
                  <strong>AccordionSummary Action Buttons</strong>: In some instances, you may want
                  to render a button or 2 inline next to the Accordion Trigger (arrow). Those
                  buttons should be icon buttons and be right aligned. For a visual reference go to
                  the following example and shrink your screen until you get a mobile view.
                  <Typography mt={0.5} mb={1.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      to="/development/framers"
                      asLink
                      id="accordion-action-button-example"
                    >
                      Example
                    </Button>
                  </Typography>
                </li>
                <li>
                  <strong>AccordionSummary Heading Styles</strong>: In some instances, you may want
                  to render the AccordionSummary text as something larger than a typical paragraph
                  font size (i.e. Punchlist titles). This can be done utilizing the <em>variant</em>{' '}
                  and <em>component</em> props of the Typography component. For a visual reference
                  go to the following example.
                  <Typography mt={0.5} mb={1.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      to="/production/punchlists/dashboard"
                      asLink
                      id="accordion-title-example"
                    >
                      Example
                    </Button>
                  </Typography>
                </li>
              </ul>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Expand/Collapse All Accordions
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              In certain layouts where there are a lot of accordions or an accordion list, it might
              be useful to provide a collapse/expand all button that toggles all related accordions.
              This is not a common practice, but is something to consider implementing, especially
              for screens like dashboards or highly referenced screens. The following is an example
              of an implementation of such a button and what that button should look like (an icon
              button to the top right of the accordion list).
            </Typography>
            <Typography mt={0.5}>
              <Button
                variant="contained"
                color="primary"
                to="/production/punchlists/dashboard"
                asLink
                id="expand-all-example"
              >
                Example
              </Button>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Detail Page - Page Titles
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              A rule of thumb to follow is to use proper wording on detail page Page Titles. Use the
              proper term <em>Add</em> or <em>Edit</em> followed by a unique identifier, if one is
              available. If a unique identifier is not available, then describe the type of content
              you are detailing (i.e. Edit Job).
            </Typography>
            <Typography mt={0.5}>
              <Stack direction="row" gap={0.5}>
                <Button
                  variant="contained"
                  color="primary"
                  to="/production/orders/order-management/178621"
                  asLink
                  id="uid-example"
                >
                  UID Example
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  to="/development/jobs/7776/general-information"
                  asLink
                  id="detail-type-example"
                >
                  Type Example
                </Button>
              </Stack>
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              MUI List Component
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              Just a reminder, lists should not be simple consecutive paragraph elements, but rather
              structured within the proper MUI List components. See the following{' '}
              <ExternalLink to="https://mui.com/material-ui/react-list/" target="_blank">
                MUI docs
              </ExternalLink>{' '}
              for details.
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Heading Hierarchy
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              Just a reminder, when implementing Typography components and headings on a page, keep
              the hierarchy in mind. The components should not only be properly set for each level,
              but the variants should also be set so that the visual hierarchy is also different. Do
              not implement an h2 the same size as the page title.
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography mb={0.5} variant="h5" component="h2" color="primary">
              Form Sectioning
            </Typography>
            <Divider sx={{ marginBottom: theme => theme.spacing(1) }} />
            <Typography>
              When sectioning forms, before utilizing MUI Typography components, you should consider
              using the Card component. The card component was specifically created for sectioning
              and grouping form fields.
            </Typography>
            <Typography mt={0.5}>
              <Button
                variant="contained"
                color="primary"
                to="/kitchen-sink/components/cards"
                asLink
                id="card-docs"
              >
                Card Docs
              </Button>
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </StyleGuidePage>
  );
};
